import React from 'react'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import MilitaryTechRoundedIcon from '@mui/icons-material/MilitaryTechRounded'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Divider from '@mui/material/Divider'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { GlassCard } from '@components/common/Glass'
import { SoloLeaderboardResult, UserSoloStats } from 'src/api/types/users'
import { helperTextColor, mainColorDarker } from 'src/ui/theme/tokens'
import { getSoloLeaderboard } from 'src/api/user'
import { useEffect, useState } from 'react'
import { headingFont, mainColor, altColorLighter, altColorDarkText } from 'src/ui/theme/tokens'

type SoloProgressModalProps = {
  open: boolean
  onClose: () => void
  soloStats?: UserSoloStats
}

const SoloProgressModal: React.FC<SoloProgressModalProps> = ({ open, onClose, soloStats }) => {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const levelBase = 100
  const currentXp = soloStats?.soloXp || 0
  const currentLevel = soloStats?.soloLevel || 1
  const nextLevel = currentLevel + 1
  const nextLevelXp = Math.max(0, Math.round(Math.pow(nextLevel - 1, 2) * levelBase))
  const currentLevelXp = Math.max(0, Math.round(Math.pow(currentLevel - 1, 2) * levelBase))
  const xpIntoLevel = Math.max(0, currentXp - currentLevelXp)
  const xpNeeded = Math.max(1, nextLevelXp - currentLevelXp)
  const xpRemaining = Math.max(0, nextLevelXp - currentXp)
  const xpProgress = Math.min(100, Math.max(0, (xpIntoLevel / xpNeeded) * 100))
  const [leaderboard, setLeaderboard] = useState<SoloLeaderboardResult | null>(null)
  const [leaderboardLoading, setLeaderboardLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLeaderboardLoading(true)
    getSoloLeaderboard(25)
      .then((res) => {
        if (res.success && res.data) {
          setLeaderboard(res.data)
        }
      })
      .finally(() => {
        setLeaderboardLoading(false)
      })
  }, [open])

  const myUserId = soloStats?.userId
  const myRank = leaderboard?.myRank
  const myEntry = leaderboard?.myEntry
  const leaders = leaderboard?.leaders || []
  const showMyRow = myEntry && !leaders.some((row) => row.userId === myEntry.userId)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isSmallScreen}
      maxWidth="sm"
      fullWidth
      sx={{
        backdropFilter: 'none',
        '& .MuiDialog-paper': {
          borderRadius: '30px',
          py: 1,
          background: '#f1f7fc',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
          <MilitaryTechRoundedIcon
            sx={{ color: mainColorDarker, fontSize: '40px' }}
            fontSize="inherit"
          />

          <Typography variant="h5" fontWeight={800} fontSize={{ xs: 24, sm: 28 }}>
            Solo Progress
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          py: 2,
          p: {
            xs: 1,
            sm: 2,
            md: 3,
          },
        }}
      >
        <Stack spacing={2.5}>
          <GlassCard
            sx={{
              p: 3,
              background: '#193b58',
              color: '#ffffff',
              '& .MuiTypography-root': { color: 'inherit' },
            }}
          >
            <Stack spacing={1.5}>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  data-rt-display
                  sx={{
                    width: 72,
                    height: 78,
                    borderRadius: '23px',
                    display: 'grid',
                    placeItems: 'center',
                    background: mainColor,
                    color: '#193b58',
                    fontFamily: headingFont,
                    fontSize: 42,
                    fontWeight: 800,
                    boxShadow: '0 4px 0 #3caccb',
                    flexShrink: 0,
                  }}
                >
                  {currentLevel}
                </Box>
                <Box>
                  <Typography variant="overline" sx={{ opacity: 0.7, letterSpacing: 1.2 }}>
                    YOUR SOLO JOURNEY
                  </Typography>
                  <Typography variant="h4" fontWeight={600}>
                    Level {currentLevel}
                  </Typography>
                  <Typography variant="body2">{currentXp.toLocaleString()} total XP</Typography>
                </Box>
              </Box>
              <Box>
                <Box display="flex" justifyContent="space-between" mb={0.5} flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    {xpIntoLevel.toLocaleString()} / {xpNeeded.toLocaleString()} XP
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {xpRemaining.toLocaleString()} to Level {nextLevel}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    borderRadius: '999px',
                    overflow: 'hidden',
                    background: 'rgba(226,232,240,0.7)',
                    border: '1px solid rgba(148,163,184,0.3)',
                  }}
                >
                  <Box
                    sx={{
                      height: 14,
                      width: `${xpProgress}%`,
                      transition: 'width 200ms ease',
                      background: '#53d9f5',
                    }}
                  />
                </Box>
              </Box>
            </Stack>
          </GlassCard>

          <Box>
            <Stack
              spacing={1.5}
              direction={{
                xs: 'column',
                sm: 'row',
              }}
            >
              <GlassCard
                sx={{
                  p: 2.5,
                  height: '100%',
                  flexGrow: 1,
                  background: altColorLighter,
                  color: altColorDarkText,
                  borderColor: '#f2d5b8',
                  boxShadow: '0 3px 0 #e8bb99',
                  '& .MuiTypography-root': { color: 'inherit' },
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Daily Streak
                </Typography>
                <Typography variant="h4" fontWeight={600}>
                  {soloStats?.dailyStreak ?? 0} day
                  {(soloStats?.dailyStreak ?? 0) > 1 ? 's' : ''}
                </Typography>
                <Typography variant="caption" color={helperTextColor} mt={0.5} fontWeight={500}>
                  Best: {soloStats?.dailyBestStreak ?? 0} day
                  {(soloStats?.dailyStreak ?? 0) > 1 ? 's' : ''}
                </Typography>
              </GlassCard>
              <GlassCard sx={{ p: 2, height: '100%', flexGrow: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Streak Bonus
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {soloStats && soloStats.dailyStreak >= 31
                    ? '+50%'
                    : soloStats && soloStats.dailyStreak >= 15
                      ? '+35%'
                      : soloStats && soloStats.dailyStreak >= 8
                        ? '+20%'
                        : soloStats && soloStats.dailyStreak >= 4
                          ? '+10%'
                          : '+0%'}
                </Typography>
                <Typography variant="caption" color={helperTextColor} mt={0.5} fontWeight={500}>
                  Daily XP bonus multiplier
                </Typography>
              </GlassCard>
            </Stack>
          </Box>

          <GlassCard sx={{ p: 0, pb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} p={2}>
              <Typography variant="subtitle1" fontWeight={600}>
                Solo Leaderboard
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {leaderboard?.total ? `${leaderboard.total} players` : 'Global'}
              </Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Player</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Level
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    XP
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaders.map((row) => {
                  const isMe = myUserId && row.userId === myUserId
                  return (
                    <TableRow
                      key={`solo-${row.userId}`}
                      sx={{
                        background: isMe ? 'rgba(0,179,255,0.08)' : 'transparent',
                      }}
                    >
                      <TableCell>{row.rank}</TableCell>
                      <TableCell>{row.username || 'Player'}</TableCell>
                      <TableCell align="right">{row.soloLevel}</TableCell>
                      <TableCell align="right">{row.soloXp.toLocaleString()}</TableCell>
                    </TableRow>
                  )
                })}
                {showMyRow ? (
                  <>
                    <TableRow>
                      <TableCell colSpan={4} sx={{ py: 1 }}>
                        <Divider />
                      </TableCell>
                    </TableRow>
                    <TableRow
                      sx={{
                        background: 'rgba(83,217,245,0.08)',
                      }}
                    >
                      <TableCell>{myRank || '-'}</TableCell>
                      <TableCell>{myEntry?.username || 'You'}</TableCell>
                      <TableCell align="right">{myEntry?.soloLevel ?? '-'}</TableCell>
                      <TableCell align="right">
                        {typeof myEntry?.soloXp === 'number'
                          ? myEntry.soloXp.toLocaleString()
                          : '-'}
                      </TableCell>
                    </TableRow>
                  </>
                ) : null}
                {!leaders.length && !leaderboardLoading ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        No leaderboard data yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
                {leaderboardLoading ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        Loading leaderboard...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </GlassCard>

          <Box>
            <Divider sx={{ mt: 2, mb: 4 }} />
            <GlassCard sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Earn More XP
              </Typography>
              <Stack spacing={1}>
                <Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Complete Daily Challenge</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      +100 XP
                    </Typography>
                  </Box>
                </Box>
                <Stack>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Rank entire trivia / poll list</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      +10 XP
                    </Typography>
                  </Box>
                  <Typography variant="caption" color={helperTextColor} textAlign="left">
                    +1 XP each item
                  </Typography>
                </Stack>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Submit a poll vote</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    +5 XP
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Rate a list / poll</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    +2 XP
                  </Typography>
                </Box>
              </Stack>
            </GlassCard>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 4, sm: 3, md: 3 },
          py: { xs: 2, md: 3 },
          bgcolor: 'transparent',
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            px: 4,
            minHeight: 48,
            fontSize: 16,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SoloProgressModal
